/**
 * The one-sentence "story" at the top of the Scorecard, built from the
 * scorecard response alone: what posts did against the period before, what
 * a post cost, and which team the joins came from.
 *
 * It only claims what the numbers can carry. A change is called a trend
 * only when both periods have enough posts to compare; a team only "drove"
 * the joins with a clear majority of a non-trivial number; an empty or
 * thin period says so instead of inventing a direction.
 */
import type { ScorecardMetrics, ScorecardTeamRow } from '@/api/insights';
import { teamLabel } from './labels';
import { money, toNum } from './insights';

/** Fewer posts than this across both periods and the change isn't called a trend. */
export const STORY_MIN_POSTS = 5;
/** Fewer joins than this and no team is said to have driven them. */
export const STORY_MIN_JOINS = 5;
/** A team "drove" the joins from this share up. */
export const STORY_DRIVER_SHARE = 0.5;

export interface StoryInput {
  current: ScorecardMetrics;
  prior: ScorecardMetrics;
  byTeam?: readonly Pick<ScorecardTeamRow, 'team' | 'teamDisplayName' | 'tracked' | 'joinedCohort'>[];
}

const plural = (n: number, one: string, many = `${one}s`): string => `${n.toLocaleString()} ${n === 1 ? one : many}`;

function postsClause(now: number, before: number): string {
  if (now + before < STORY_MIN_POSTS) {
    return `${plural(now, 'post')} this period (${before.toLocaleString()} the period before), too few to call a trend`;
  }
  if (before === 0) return `${plural(now, 'post')}, up from none the period before`;
  const change = Math.round(((now - before) / before) * 100);
  if (change === 0) return `Posts flat at ${now.toLocaleString()}`;
  return `Posts ${change > 0 ? 'up' : 'down'} ${Math.abs(change)}% on last period (${before.toLocaleString()} → ${now.toLocaleString()})`;
}

function costClause(current: ScorecardMetrics, prior: ScorecardMetrics): string | null {
  const cpp = toNum(current.costPerPost);
  const prevCpp = toNum(prior.costPerPost);
  const cash = toNum(current.cashSpent) ?? 0;
  if (cpp === null) {
    return cash > 0 ? `${money(cash)} paid out with no posts in the period yet` : null;
  }
  // Cost per post on one or two posts swings wildly; quote it, don't compare it.
  const comparable = prevCpp !== null && current.postedActivity >= 3 && prior.postedActivity >= 3;
  if (!comparable) return `cost per post ${money(cpp)}`;
  const change = (cpp - prevCpp) / prevCpp;
  if (Math.abs(change) < 0.05) return `cost per post steady at ${money(cpp)}`;
  return `cost per post ${change < 0 ? 'down' : 'up'} to ${money(cpp)} (from ${money(prevCpp)})`;
}

function driverClause(rows: StoryInput['byTeam']): string | null {
  const tracked = (rows ?? []).filter((r) => r.tracked);
  if (tracked.length < 2) return null;
  const total = tracked.reduce((s, r) => s + r.joinedCohort, 0);
  if (total < STORY_MIN_JOINS) return null;
  const top = [...tracked].sort((a, b) => b.joinedCohort - a.joinedCohort)[0]!;
  const share = top.joinedCohort / total;
  if (share < STORY_DRIVER_SHARE) return 'joins spread across teams';
  return `${teamLabel(top.team, top.teamDisplayName)} drove ${Math.round(share * 100)}% of joins`;
}

/** One sentence. Never empty. */
export function scorecardStory(input: StoryInput): string {
  const { current, prior } = input;
  const busy = (m: ScorecardMetrics): boolean =>
    m.messages + m.replied + m.postedActivity + m.joinedCohort > 0 || (toNum(m.cashSpent) ?? 0) > 0;

  if (!busy(current)) {
    if (!busy(prior)) return 'Nothing recorded in this period or the one before it yet.';
    return `Nothing recorded in this period yet. The period before had ${plural(prior.replied, 'reply', 'replies')} and ${plural(prior.postedActivity, 'post')}.`;
  }

  const clauses = [
    postsClause(current.postedActivity, prior.postedActivity),
    costClause(current, prior),
    driverClause(input.byTeam),
  ].filter((c): c is string => !!c);
  const sentence = clauses.join('; ');
  return `${sentence.charAt(0).toUpperCase()}${sentence.slice(1)}.`;
}
